import { useEffect, useMemo, useState } from 'react';
import { getGradeLabel } from '@car-pool/shared/schoolRecords';
import { apiFetch } from '../config/api';

const FALLBACK_GRADE_CODES = [-3, -2, -1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

function isActive(value) {
  return value === true || value === 1 || String(value) === '1';
}

function buildGradeOptions(gradeNames = [], splits = []) {
  const namedGrades = gradeNames
    .filter((grade) => isActive(grade.is_active))
    .map((grade) => ({
      gradeCode: Number(grade.grade_code),
      label: grade.label || getGradeLabel(grade.grade_code),
    }))
    .filter((grade) => Number.isInteger(grade.gradeCode));

  const splitGrades = splits
    .map((split) => Number(split.grade))
    .filter((gradeCode) => Number.isInteger(gradeCode));

  const source = namedGrades.length
    ? namedGrades
    : [...new Set([...splitGrades, ...FALLBACK_GRADE_CODES])]
      .sort((left, right) => left - right)
      .map((gradeCode) => ({ gradeCode, label: getGradeLabel(gradeCode) }));

  return [...new Map(source.map((grade) => [grade.gradeCode, grade])).values()]
    .sort((left, right) => left.gradeCode - right.gradeCode);
}

export default function GradeSplitManager({
  token,
  schoolId,
  gradeNames = [],
  splits = [],
  onChange,
}) {
  const gradeOptions = useMemo(() => buildGradeOptions(gradeNames, splits), [gradeNames, splits]);
  const [gradeCode, setGradeCode] = useState('');
  const [splitCode, setSplitCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (gradeCode || !gradeOptions.length) return;
    setGradeCode(String(gradeOptions[0].gradeCode));
  }, [gradeCode, gradeOptions]);

  const gradeLabelMap = useMemo(
    () => Object.fromEntries(gradeOptions.map((grade) => [grade.gradeCode, grade.label])),
    [gradeOptions]
  );

  const groupedSplits = useMemo(() => {
    const groups = new Map();

    gradeOptions.forEach((grade) => {
      groups.set(grade.gradeCode, {
        ...grade,
        splits: [],
      });
    });

    splits.forEach((split) => {
      const grade = Number(split.grade);
      if (!groups.has(grade)) {
        groups.set(grade, {
          gradeCode: grade,
          label: getGradeLabel(grade, gradeLabelMap),
          splits: [],
        });
      }
      groups.get(grade).splits.push(split);
    });

    return [...groups.values()]
      .map((group) => ({
        ...group,
        splits: [...group.splits].sort((left, right) => String(left.split_code || '').localeCompare(String(right.split_code || ''))),
      }))
      .filter((group) => group.splits.length > 0)
      .sort((left, right) => left.gradeCode - right.gradeCode);
  }, [gradeLabelMap, gradeOptions, splits]);

  const handleCreate = async (event) => {
    event.preventDefault();
    const cleanedCode = splitCode.trim();
    const parsedGrade = Number(gradeCode);

    if (!Number.isInteger(parsedGrade) || !cleanedCode) return;

    setSaving(true);
    setError('');

    try {
      await apiFetch('/school-grade-splits', {
        method: 'POST',
        body: JSON.stringify({
          school_id: Number(schoolId),
          grade: parsedGrade,
          split_code: cleanedCode,
          is_active: true,
        }),
      }, token);
      setSplitCode('');
      await onChange?.();
    } catch (err) {
      setError(err.message || 'Failed to add split');
    } finally {
      setSaving(false);
    }
  };

  const toggleSplit = async (split) => {
    setSaving(true);
    setError('');

    try {
      await apiFetch(`/school-grade-splits/${split.id}/active`, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: !isActive(split.is_active) }),
      }, token);
      await onChange?.();
    } catch (err) {
      setError(err.message || 'Failed to update split');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="sap-parents-panel sgs-manager ssp-collapsible-panel">
      <button
        type="button"
        className="sap-panel-heading ssp-collapsible-heading"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-controls="sgs-manager-content"
      >
        <div>
          <h2>Grade Splits</h2>
          <p>Add grade variations such as bunk, homeroom, boys, girls, or class sections.</p>
        </div>
        <span className={`ssp-collapse-icon ${open ? 'open' : ''}`} aria-hidden="true"></span>
      </button>

      {open ? (
        <div id="sgs-manager-content" className="ssp-collapsible-content">
          <form className="sgs-form" onSubmit={handleCreate}>
            <div className="sa-field">
              <label>Grade</label>
              <select value={gradeCode} onChange={(event) => setGradeCode(event.target.value)} disabled={saving || !gradeOptions.length}>
                {gradeOptions.map((grade) => (
                  <option key={grade.gradeCode} value={grade.gradeCode}>{grade.label}</option>
                ))}
              </select>
            </div>
            <div className="sa-field">
              <label>Split Name</label>
              <input
                value={splitCode}
                onChange={(event) => setSplitCode(event.target.value)}
                maxLength={20}
                placeholder="Bunk 09"
                disabled={saving}
              />
            </div>
            <button type="submit" className="sa-btn sa-btn-primary" disabled={saving || !gradeCode || !splitCode.trim()}>
              {saving ? 'Saving...' : 'Add Split'}
            </button>
          </form>

          {error ? <div className="sa-error">{error}</div> : null}

          <div className="sgs-list">
            {groupedSplits.length === 0 ? (
              <div className="sa-empty sap-compact-empty">No grade splits configured yet.</div>
            ) : groupedSplits.map((group) => (
              <div key={group.gradeCode} className="sgs-grade-group">
                <div className="sgs-grade-title">{group.label}</div>
                <div className="sgs-chip-row">
                  {group.splits.map((split) => {
                    const active = isActive(split.is_active);
                    return (
                      <button
                        key={split.id}
                        type="button"
                        className={`sgs-chip ${active ? 'active' : 'inactive'}`}
                        onClick={() => toggleSplit(split)}
                        disabled={saving}
                        title={active ? 'Deactivate split' : 'Reactivate split'}
                      >
                        <span>{split.split_code}</span>
                        <strong>{active ? 'Active' : 'Inactive'}</strong>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            </div>
        </div>
      ) : null}
    </section>
  );
}
